import { Dialog } from '@headlessui/react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';

import styles from './PhotoStudioLanding.module.css';

import PhotoPacksShowcase from '@/components/landing/ImageTools/PhotoPacksShowcase';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';

import { PHOTO_PACK_COVERS } from '@/constants/photo_pack_covers';
import { PhotoPack } from '@/constants/photo_packs';

const Footer = dynamic(() => import('@/components/layout/Footer'));
const heroPhotos = [
  { slug: 'instagram', label: 'look_day' },
  { slug: 'corporate-executive', label: 'look_work' },
  { slug: 'summer-in-paris', label: 'look_evening' },
] as const;
const videoExamples = [
  {
    key: 'product',
    src: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_captioned_9ff3a294-d0ad-4ebe-bf3a-c850d3e9c2e4.mp4',
  },
  {
    key: 'influencer',
    src: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/f0fff81b-37ee-40fc-9e05-7d5c38f437b7.mp4',
  },
  {
    key: 'avatar',
    src: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_9c77ff75-512e-40d9-92c9-d7e7ddb8a1b8.mp4',
  },
] as const;

function Arrow({ down = false }: { down?: boolean }) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.6'
      aria-hidden='true'
    >
      <path d={down ? 'M12 4v15m-6-6 6 6 6-6' : 'M4 12h15m-6-6 6 6-6 6'} />
    </svg>
  );
}

export default function PhotoStudioLanding({
  photoPacks,
}: {
  photoPacks: PhotoPack[];
}) {
  const { t, lang } = useTranslation('ai-photo-generator');
  const [preview, setPreview] = useState<(typeof heroPhotos)[number] | null>(
    null,
  );
  const [videoIndex, setVideoIndex] = useState(0);
  const video = videoExamples[videoIndex];

  return (
    <div className={styles.page} lang={lang}>
      <Seo
        templateTitle={t('AI Photo Studio | AI Photo Generator')}
        description={t('studio.hero_description')}
        image={PHOTO_PACK_COVERS.instagram}
        structuredData={false}
      />
      <a href='#studio-main' className={styles.skipLink}>
        {t('studio.skip')}
      </a>
      <LandingPageNavigation />
      <main id='studio-main' dir={lang === 'ar' ? 'rtl' : undefined}>
        <div className={styles.container}>
          <nav className={styles.sectionNav} aria-label={t('AI Photo Studio')}>
            <span className={styles.studioName}>{t('AI Photo Studio')}</span>
            <div>
              <a href='#photo-packs'>{t('studio.nav_packs')}</a>
              <a href='#how-it-works'>{t('How It Works')}</a>
              <a href='#photo-to-video'>{t('studio.nav_video')}</a>
            </div>
          </nav>

          <section className={styles.hero} aria-labelledby='studio-title'>
            <div className={styles.heroCopy}>
              <p className={styles.eyebrow}>{t('studio.eyebrow')}</p>
              <h1 id='studio-title'>
                {t('studio.hero_title')} <em>{t('studio.hero_accent')}</em>
              </h1>
              <p className={styles.intro}>{t('studio.hero_description')}</p>
              <div className={styles.actions}>
                <Link href='/ai-images/models' className={styles.primaryButton}>
                  {t('studio.create')}
                  <Arrow />
                </Link>
                <a href='#photo-packs' className={styles.textLink}>
                  {t('studio.browse')}
                  <Arrow down />
                </a>
              </div>
              <p className={styles.heroNote}>{t('studio.hero_note')}</p>
            </div>

            <div className={styles.heroGallery}>
              {heroPhotos.map((photo, index) => (
                <figure
                  key={photo.slug}
                  className={
                    index === 0 ? styles.heroMainPhoto : styles.heroSmallPhoto
                  }
                >
                  <button
                    type='button'
                    onClick={() => setPreview(photo)}
                    aria-label={t('studio.image_button', {
                      look: t(`studio.${photo.label}`),
                    })}
                  >
                    <Image
                      src={PHOTO_PACK_COVERS[photo.slug]!}
                      alt={t(`studio.${photo.label}`)}
                      fill
                      sizes={
                        index === 0
                          ? '(max-width: 767px) 58vw, 360px'
                          : '(max-width: 767px) 28vw, 190px'
                      }
                      preload={index === 0}
                    />
                    <span className={styles.enlarge} aria-hidden='true'>
                      ↗
                    </span>
                  </button>
                  <figcaption>
                    <span>{`0${index + 1}`}</span>
                    {t(`studio.${photo.label}`)}
                  </figcaption>
                </figure>
              ))}
              <p className={styles.exampleLabel}>{t('studio.examples')}</p>
            </div>
          </section>
        </div>

        <section
          id='how-it-works'
          className={`${styles.workflow} ${styles.container}`}
          aria-labelledby='workflow-title'
        >
          <div className={styles.workflowIntro}>
            <p className={styles.eyebrow}>{t('How It Works')}</p>
            <h2 id='workflow-title'>
              {t('studio.works_title')} <em>{t('studio.works_accent')}</em>
            </h2>
            <p className={styles.sectionDescription}>
              {t('studio.works_description')}
            </p>
            <div className={styles.workflowPhotos}>
              {['coffee-shop', 'outdoor-adventure'].map((slug) => (
                <Image
                  key={slug}
                  src={PHOTO_PACK_COVERS[slug]!}
                  alt={
                    photoPacks.find((pack) => pack.slug === slug)?.name || slug
                  }
                  width={320}
                  height={427}
                  sizes='(max-width: 767px) 40vw, 220px'
                />
              ))}
            </div>
            <p className={styles.photoCaption}>
              {t('studio.workflow_caption')}
            </p>
          </div>
          <ol className={styles.steps}>
            {[1, 2, 3].map((step) => (
              <li key={step}>
                <span className={styles.stepNumber} aria-hidden='true'>
                  0{step}
                </span>
                <div>
                  <h3>{t(`studio.step_${step}_title`)}</h3>
                  <p>{t(`studio.step_${step}_description`)}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <div className={styles.collection}>
          <div className={styles.container}>
            <PhotoPacksShowcase
              photoPacks={photoPacks}
              initialCount={12}
              className={styles.packCollection}
            />
          </div>
        </div>

        <section
          id='photo-to-video'
          className={`${styles.videoSection} ${styles.container}`}
          aria-labelledby='video-title'
        >
          <div className={styles.videoCopy}>
            <p className={styles.eyebrow}>{t('studio.nav_video')}</p>
            <h2 id='video-title'>
              {t('studio.video_title')} <em>{t('studio.video_accent')}</em>
            </h2>
            <p className={styles.sectionDescription}>
              {t('studio.video_description')}
            </p>
            <div
              className={styles.videoChoices}
              role='group'
              aria-label={t('studio.nav_video')}
            >
              {videoExamples.map((example, index) => (
                <button
                  key={example.key}
                  type='button'
                  aria-pressed={videoIndex === index}
                  aria-controls='video-example'
                  onClick={() => setVideoIndex(index)}
                >
                  <span aria-hidden='true'>0{index + 1}</span>
                  <span>{t(`studio.video_${example.key}`)}</span>
                  <Arrow />
                </button>
              ))}
            </div>
            <Link href='/ai-images/models' className={styles.textLink}>
              {t('studio.create')}
              <Arrow />
            </Link>
          </div>
          <figure id='video-example' className={styles.videoExample}>
            <video
              key={video.src}
              controls
              playsInline
              preload='metadata'
              aria-label={t(`studio.video_${video.key}`)}
            >
              <source src={video.src} type='video/mp4' />
            </video>
            <figcaption>
              {t(`studio.video_${video.key}_description`)}
            </figcaption>
          </figure>
        </section>

        <section
          className={`${styles.faqSection} ${styles.container}`}
          aria-labelledby='faq-title'
        >
          <h2 id='faq-title'>{t('studio.faq_title')}</h2>
          <div className={styles.faqList}>
            {[1, 2, 3].map((item) => (
              <details key={item}>
                <summary>
                  {t(`studio.faq_${item}_question`)}
                  <span aria-hidden='true'>+</span>
                </summary>
                <p>{t(`studio.faq_${item}_answer`)}</p>
              </details>
            ))}
          </div>
        </section>

        <section className={styles.finalSection} aria-labelledby='final-title'>
          <div className={`${styles.finalInner} ${styles.container}`}>
            <div>
              <h2 id='final-title'>
                {t('studio.cta_title')} <em>{t('studio.cta_accent')}</em>
              </h2>
              <p>{t('studio.cta_description')}</p>
            </div>
            <Link
              href='/ai-images/models/train'
              className={styles.primaryButton}
            >
              {t('studio.create')}
              <Arrow />
            </Link>
          </div>
        </section>
      </main>
      <Footer />

      <Dialog
        open={preview !== null}
        onClose={() => setPreview(null)}
        className={styles.previewDialog}
        lang={lang}
      >
        <div className={styles.previewBackdrop} aria-hidden='true' />
        <div className={styles.previewFrame}>
          <Dialog.Panel className={styles.previewPanel}>
            <button
              type='button'
              className={styles.previewClose}
              onClick={() => setPreview(null)}
              aria-label={t('studio.close')}
            >
              ×
            </button>
            {preview ? (
              <>
                <Image
                  src={PHOTO_PACK_COVERS[preview.slug]!}
                  alt={t(`studio.${preview.label}`)}
                  width={1086}
                  height={1448}
                  sizes='(max-width: 767px) 90vw, 600px'
                />
                <Dialog.Title className={styles.previewTitle}>
                  {t(`studio.${preview.label}`)}
                </Dialog.Title>
              </>
            ) : null}
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}
